import styled from 'styled-components';
// import FilterIcon from '@/assets/mo/Filter.svg?react';
import { useState } from 'react';
import ReviewTag from '@/components/mo/Review/ReviewTag';
import ReviewMetaTag from '@/components/mo/Review/ReviewMetaTag';
import { useQuery } from '@tanstack/react-query';
import { fetchReviewList } from '@/api/review';
import { useNavigate } from 'react-router-dom';
import EyesIcon from '@/assets/mo/eyes.svg?react';

interface FilterBarProps {
  onOpenModal: () => void;
  selectedFilters: {
    보험종류: string[];
    사고유형: string[];
  };
}

const ReviewPrompt = ({ selectedFilters }: FilterBarProps) => {
  const [sort] = useState<'recent' | 'view'>('recent');
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(5);

  const handleMore = () => {
    setVisibleCount((prev) => prev + 5);
  };

  const handleClick = () => {
    navigate('/review');
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const baseInsuranceTags = ['실손보험', '자동차보험', '배상책임', '진단금/기타'];
  const baseAccidentTags = [
    '교통사고',
    '상해사고',
    '후유장해',
    '진단금',
    '누수',
    '도수/체외충격파',
    '백내장',
    '고지의무',
    '전립선',
    '맘모톰',
    '하이푸',
    '비밸브/비중격',
    '무릎치료',
    '어린이',
  ];

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reviewList', sort, selectedFilters],
    queryFn: () => {
      const insuranceTags = selectedFilters.보험종류.includes('기타')
        ? []
        : selectedFilters.보험종류.filter((tag) => tag !== '전체');

      const accidentTags = selectedFilters.사고유형.includes('기타')
        ? []
        : selectedFilters.사고유형.filter((tag) => tag !== '전체');

      const allTags = [...insuranceTags, ...accidentTags];
      const tags = allTags.length > 0 ? allTags : undefined;

      return fetchReviewList({
        page: 0,
        size: 200,
        sort: [sort === 'recent' ? 'incidentDate,desc' : 'viewCount,desc'],
        tags,
        keyword: '',
      });
    },
  });

  const reviewList = data?.content ?? [];

  const filteredReviewList = reviewList.filter((review) => {
    const tagList = review.tags.map((tag) => tag.tag);

    const hasBaseInsuranceTag = tagList.some((tag) => baseInsuranceTags.includes(tag));
    const hasBaseAccidentTag = tagList.some((tag) => baseAccidentTags.includes(tag));

    const insuranceCondition = selectedFilters.보험종류.includes('기타')
      ? !hasBaseInsuranceTag
      : true;

    const accidentCondition = selectedFilters.사고유형.includes('기타')
      ? !hasBaseAccidentTag
      : true;

    return insuranceCondition && accidentCondition;
  });

  console.log(
    'incidentDate:',
    reviewList.map((r) => r.incidentDate),
  );
  if (isLoading) return <div>로딩 중...</div>;
  if (isError) return <div>에러 발생</div>;

  return (
    <>
      <ReviewPromptWrapper>
        <ReviewPromptText>나와 비슷한 후기를 찾아보세요</ReviewPromptText>
        <MoreButton>
          <MoreText onClick={handleClick}>더보기</MoreText>
        </MoreButton>
      </ReviewPromptWrapper>
      <Wrapper>
        <InnerColumn>
          {filteredReviewList.length === 0 ? (
            <NoDataMessage>데이터가 없습니다.</NoDataMessage>
          ) : (
            filteredReviewList.slice(0, visibleCount).map((review) => (
              <CardWrapper key={review.id} onClick={() => navigate(`/review/${review.id}`)}>
                <CardTopRow>
                  <CardTopTags>
                    {review.tags.map((tag) => (
                      <ReviewTag key={tag.id} label={`#${tag.tag}`} />
                    ))}
                  </CardTopTags>
                  <ViewCountWrapper>
                    <EyesIcon />
                    <ViewCountText>{review.viewCount.toLocaleString()}</ViewCountText>
                  </ViewCountWrapper>
                </CardTopRow>
                <CardContents>
                  <CardTitle>{review.title}</CardTitle>
                  <CardDesc>{review.content}</CardDesc>
                </CardContents>
                <MetaRow>
                  <ReviewMetaTag
                    label={
                      review.issues[0]?.isPayoutApproved
                        ? review.issues[0]?.totalFee
                          ? `${Number(review.issues[0].totalFee).toLocaleString()}만원 지급`
                          : '지급'
                        : '부지급'
                    }
                  />
                  <ReviewDate>
                    {review.incidentDate
                      ? new Date(review.incidentDate).toLocaleDateString('ko-KR')
                      : ''}
                  </ReviewDate>
                </MetaRow>
              </CardWrapper>
            ))
          )}
        </InnerColumn>
        {filteredReviewList.length > visibleCount && (
          <LoadMoreButton onClick={handleMore}>
            <LoadMoreText>더보기</LoadMoreText>
          </LoadMoreButton>
        )}
      </Wrapper>
    </>
  );
};

export default ReviewPrompt;

const ReviewPromptWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const ReviewPromptText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 18px;
  font-style: normal;
  font-weight: 800;
  line-height: 120%;
  letter-spacing: -0.28px;
`;

const MoreButton = styled.div`
  display: flex;
  padding: 18px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

const MoreText = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;

  color: var(--gray-ui_gray05, #999);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.2px;
  cursor: pointer;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const InnerColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
`;

const CardWrapper = styled.div`
  width: 100%;
  display: flex;
  padding: 20px 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-bottom: 1px solid var(--table-line-table_line03, #d1d1d1);
  cursor: pointer;
`;

const CardTopTags = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;

  & > div {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const CardContents = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const CardTitle = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;
  overflow: hidden;
  color: var(--gray-ui_gray01, #191919);
  text-overflow: ellipsis;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.36px;
  text-align: start;
`;

const CardDesc = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  color: var(--gray-ui_gray03, #3a3a3a);
  text-overflow: ellipsis;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: -0.28px;
  text-align: start;
`;

// const SelectedFilterRow = styled.div`
//   display: flex;
//   height: 50px;
//   padding: 12px 0px;
//   flex-wrap: wrap;
//   align-items: center;
//   gap: 4px;
// `;

// const FilterTag = styled.div`
//   display: flex;
//   padding: 4px 8px;
//   justify-content: center;
//   align-items: center;
//   border-radius: 99px;
//   border: 1px solid var(--table-line-table_line03, #d1d1d1);
//   background: var(--Primary-white, #fff);
//   color: var(--gray-ui_gray04, #6f6f6f);
//   font-family: 'Spoqa Han Sans Neo';
//   font-size: 12px;
//   font-weight: 500;
//   line-height: 120%;
//   letter-spacing: -0.24px;
// `;

const NoDataMessage = styled.div`
  width: 100%;
  padding: 40px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

const LoadMoreButton = styled.div`
  display: flex;
  padding: 14px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background: #f3f3f3;
  border-radius: 999px;
  margin: 24px auto 0;
  cursor: pointer;
`;

const LoadMoreText = styled.div`
  color: var(--gray-ui_gray05, #999);
  font-size: 14px;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.2px;
`;

const CardTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  width: 100%;
`;

const ViewCountWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ViewCountText = styled.span`
  color: var(--gray-ui_gray05, #999);
  text-align: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 16.8px */
  letter-spacing: -0.28px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

// const ReviewDate = styled.div`
//   color: var(--gray-ui_gray05, #999);
//   text-align: center;
//   font-family: 'Spoqa Han Sans Neo';
//   font-size: 14px;
//   font-style: normal;
//   font-weight: 400;
//   line-height: 120%; /* 16.8px */
//   letter-spacing: -0.28px;
// `;

const ReviewDate = styled.div`
  color: var(--gray-ui_gray05, #999);
  text-align: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 16.8px */
  letter-spacing: -0.28px;
`;
